import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useAuthStore } from '@/store/authStore';
import {
  saveActionItem,
  type ActionItemPriority,
  type ActionItemStatus,
} from '@/services/db/actionItems';
import { FieldLabel, ChipGroup, type ChipOption } from './_shared';

const PRIORITY_OPTIONS: ChipOption<ActionItemPriority>[] = [
  { value: 'low',      label: 'Low',      color: '#3ba870' },
  { value: 'medium',   label: 'Medium',   color: '#00b4b4' },
  { value: 'high',     label: 'High',     color: '#d87820' },
  { value: 'critical', label: 'Critical', color: '#c0392b' },
];

export type ActionItemSourceSection =
  | 'jsa_evaluations'
  | 'swa_events'
  | 'safety_events'
  | 'good_catch';

interface Props {
  dsrId: string;
  sourceSection: ActionItemSourceSection;
  defaultDescription: string;
  defaultPriority?: ActionItemPriority;
  /** Override button label. Default: "→ Action Item". */
  buttonLabel?: string;
  /** Optional: disable the button (e.g. read-only DSR). */
  disabled?: boolean;
  className?: string;
}

export default function CreateActionItemButton({
  dsrId,
  sourceSection,
  defaultDescription,
  defaultPriority = 'medium',
  buttonLabel = '→ Action Item',
  disabled,
  className,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(defaultDescription);
  const [assignedToName, setAssignedToName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<ActionItemPriority>(defaultPriority);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const openSheet = () => {
    setDescription(defaultDescription);
    setAssignedToName('');
    setDueDate('');
    setPriority(defaultPriority);
    setError(null);
    setOpen(true);
  };

  const save = async () => {
    if (!user || !tenantId) return;
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveActionItem({
        dsrReportId: dsrId,
        tenantId,
        userId: user.id,
        description: description.trim(),
        assignedToName: assignedToName.trim() || null,
        dueDate: dueDate || null,
        priority,
        status: 'open' as ActionItemStatus,
        resolutionNotes: null,
        sourceSection,
      });
      queryClient.invalidateQueries({ queryKey: ['action_items', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
      setOpen(false);
      setToast('Action item created');
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={openSheet}
        className={`border-mozaic-teal text-mozaic-teal hover:bg-mozaic-teal/10 ${className ?? ''}`}
      >
        <ClipboardCheck className="mr-1 h-3.5 w-3.5" /> {buttonLabel}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex flex-col overflow-y-auto p-0">
          <SheetHeader className="border-b">
            <SheetTitle>Create Action Item</SheetTitle>
            <SheetDescription>
              Pre-filled from {sourceSection.replace('_', ' ')}. Edit any field before saving.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-4 p-4">
            <div className="space-y-1">
              <FieldLabel required>Description</FieldLabel>
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What needs to be done?"
              />
            </div>
            <div className="space-y-1">
              <FieldLabel>Assigned To</FieldLabel>
              <Input
                value={assignedToName}
                onChange={(e) => setAssignedToName(e.target.value)}
                placeholder="e.g. J. Smith"
              />
            </div>
            <div className="space-y-1">
              <FieldLabel>Due Date</FieldLabel>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <FieldLabel required>Priority</FieldLabel>
              <ChipGroup
                options={PRIORITY_OPTIONS}
                value={priority}
                onChange={(v) => v && setPriority(v as ActionItemPriority)}
                wrap
              />
            </div>
            {error && <p className="text-xs text-mozaic-red">{error}</p>}
          </div>

          <div className="flex justify-end gap-2 border-t p-4">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-mozaic-teal hover:bg-mozaic-teal/90"
              onClick={save}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save Action Item'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {toast && (
        <div
          role="status"
          className="fixed right-4 top-4 z-[60] rounded-md border border-mozaic-teal/40 bg-mozaic-teal px-4 py-2 text-sm font-medium text-white shadow-lg"
        >
          {toast}
        </div>
      )}
    </>
  );
}
